import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useI18n, type Lang } from '../i18n'
import { useAuth } from '../auth'
import { supabase } from '../lib/supabase'
import { Button, Card, Field, Input, Sheet } from '../ui/kit'
import { Logo } from '../ui/Logo'

/**
 * Konto-Löschung in der App (Vorgang 019, Apple-Guideline 5.1.1(v)).
 *
 * Zwei Bildschirme, eine Datei, weil sie dieselben Fakten zeigen:
 *
 *   · `KontoLoeschenEintrag` — der Eintrag im Konto-Block der Einstellungen samt
 *     Löschblatt: Vorschau (`konto_loeschung_vorschau()`), Aufklärung, Warnung
 *     beim einzigen Owner, Abo-Hinweis je Kanal, Passwort-Bestätigung,
 *     Vormerkung (`konto_loeschung_vormerken()`), Abmeldung.
 *   · `KontoLoeschungGate` — der AK5-Dialog beim Anmelden während der Karenz:
 *     „Löschung abbrechen / Löschung beibehalten".
 *
 * Die Passwort-Abfrage ist Geräteschutz, kein Sicherheitstor (ADR 019): das
 * eigentliche Netz gegen eine unerwünschte Löschung ist die 30-Tage-Karenz mit
 * dem Abbruch im Gate. Beide RPCs sind serverseitig idempotent (Auflagen A1/A2)
 * — ein Doppelklick erzeugt weder eine zweite Vormerkung noch einen Fehler.
 */

interface VorschauMandant {
  tenant_id: string
  name: string | null
  rolle: string
  einziger_owner: boolean
  weitere_mitglieder: number
  abo_status: string | null
  abo_kanal: string | null
}

interface Vorschau {
  karenz_tage: number
  mandanten: VorschauMandant[]
}

/**
 * Das Fristende ist ein `timestamptz`, kein Kalendertag. `kalendertag`/`formatDate`
 * schneiden am UTC-Datum ab — eine Vormerkung kurz nach Mitternacht Wiener Zeit
 * nennte dann den Vortag (Testplan 019, T27). Deshalb hier ausdrücklich mit
 * Zeitzone formatiert.
 */
function fristDatum(iso: string, lang: Lang): string {
  return new Intl.DateTimeFormat(lang === 'de' ? 'de-AT' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Vienna' })
    .format(new Date(iso))
}

/** Der Eintrag im Konto-Block — nur im Live-Modus gerendert (AK1, SettingsView). */
export function KontoLoeschenEintrag() {
  const { t } = useI18n()
  const [offen, setOffen] = useState(false)
  return (
    <>
      <Button variant="ghost" className="text-danger" onClick={() => setOffen(true)}>
        <Trash2 size={16} /> {t('kontoLoeschung.eintrag')}
      </Button>
      {offen && <KontoLoeschenSheet onClose={() => setOffen(false)} />}
    </>
  )
}

function KontoLoeschenSheet({ onClose }: { onClose: () => void }) {
  const { t, lang } = useI18n()
  const { reauth, signOut } = useAuth()
  const [vorschau, setVorschau] = useState<Vorschau | null>(null)
  const [vorschauFehlt, setVorschauFehlt] = useState(false)
  const [passwort, setPasswort] = useState('')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  // Nach erfolgreicher Vormerkung: das Fristende aus der RPC-Rückgabe. Ab hier
  // führt JEDER Weg aus dem Blatt in die Abmeldung (AK4) — auch das Schließen.
  const [frist, setFrist] = useState<string | null>(null)

  const laden = useCallback(() => {
    if (!supabase) return
    setVorschauFehlt(false)
    supabase.rpc('konto_loeschung_vorschau').then(({ data, error }) => {
      if (error || !data) { setVorschauFehlt(true); return }
      setVorschau(data as Vorschau)
    })
  }, [])
  useEffect(() => { laden() }, [laden])

  const bestaetigen = async () => {
    if (!supabase) return
    setBusy(true)
    setFehler(null)
    try {
      // AK2: falsches Passwort → abgewiesen, kein RPC-Aufruf, keine Vormerkung.
      // Die Meldung ist generisch — sie unterscheidet die Ablehnungsgründe nicht.
      await reauth(passwort)
    } catch {
      setFehler(t('kontoLoeschung.reauthFehler'))
      setBusy(false)
      return
    }
    const { data, error } = await supabase.rpc('konto_loeschung_vormerken')
    // Die NULL-Randlage ist benannt (Wettlauf zweier Vormerkungen): Fehlermeldung
    // statt Absturz; der nächste Versuch liefert idempotent das Fristende (A1).
    if (error || !data) {
      setFehler(t('kontoLoeschung.vormerkenFehler'))
      setBusy(false)
      return
    }
    setFrist(String(data))
    setBusy(false)
  }

  // AK4: nach der Vormerkung wird abgemeldet. Das Blatt zeigt vorher die
  // Bestätigung mit dem Fristende; wer es stattdessen einfach schließt, wird
  // ebenfalls abgemeldet — die Vormerkung besteht, und ein „weiterarbeiten wie
  // immer" wäre die halbe Wahrheit.
  const schliessen = () => {
    // B2 (Prüfbefund): Während der laufende vormerken-Aufruf unterwegs ist,
    // schließt NICHTS das Blatt — sonst entstünde die Vormerkung serverseitig,
    // ohne dass der Nutzer Bestätigung und Fristende sieht (AK4).
    if (busy) return
    if (frist !== null) { void signOut(); return }
    onClose()
  }

  const abo = (m: VorschauMandant) =>
    m.abo_status === 'trialing' || m.abo_status === 'active'
      // A4-Eichung wie auf dem Server: ein leerer Kanal ist die Stripe-Lesart.
      ? (m.abo_kanal ?? 'stripe')
      : null

  return (
    <Sheet
      open
      onClose={schliessen}
      title={t('kontoLoeschung.titel')}
      footer={frist !== null
        ? (
          <Button variant="primary" className="flex-1" onClick={() => void signOut()}>
            {t('kontoLoeschung.abmelden')}
          </Button>
        )
        : (
          <>
            <Button variant="ghost" className="flex-1" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => void bestaetigen()}
              disabled={busy || !vorschau || passwort === ''}
            >
              <Trash2 size={16} /> {t('kontoLoeschung.bestaetigen')}
            </Button>
          </>
        )}
    >
      {frist !== null ? (
        <div className="space-y-3 text-sm">
          <p className="font-medium">
            {t('kontoLoeschung.fertig', { datum: fristDatum(frist, lang) })}
          </p>
          <p>{t('kontoLoeschung.fertigAbmeldung')}</p>
        </div>
      ) : vorschauFehlt ? (
        <div className="space-y-3 text-sm">
          <p className="bg-warn-soft text-warn rounded-xl px-3 py-2">
            {t('kontoLoeschung.vorschauFehler')}
          </p>
          <Button onClick={laden}>{t('kontoLoeschung.erneut')}</Button>
        </div>
      ) : !vorschau ? (
        <p className="text-sm text-muted">{t('common.loading')}</p>
      ) : (
        <div className="space-y-3 text-sm">
          <p>{t('kontoLoeschung.intro', { tage: vorschau.karenz_tage })}</p>
          <p>{t('kontoLoeschung.neuStartLeer')}</p>
          <p className="text-muted">{t('kontoLoeschung.belegeBleiben')}</p>

          {/* Entscheid 2: der einzige Owner sieht die Folgen, gesperrt wird er
              nicht — es ist sein Betrieb. Warnung je Mandant, denn die Vorschau
              kann mehrere nennen. */}
          {vorschau.mandanten.filter((m) => m.einziger_owner).map((m) => (
            <p key={m.tenant_id} className="bg-warn-soft text-warn rounded-xl px-3 py-2">
              {t('kontoLoeschung.betriebZu', { name: m.name ?? '—' })}
              {m.weitere_mitglieder === 1 && <> {t('kontoLoeschung.mitgliedVerliert')}</>}
              {m.weitere_mitglieder > 1 && (
                <> {t('kontoLoeschung.mitgliederVerlieren', { n: m.weitere_mitglieder })}</>
              )}
            </p>
          ))}

          {/* AK3: der Kündigungsort je Kanal — und die Löschung bleibt trotzdem
              durchführbar; diese Hinweise sperren nichts. NUR bei einziger_owner
              (B1, Prüfbefund): Der Vollzug beendet Abos ausschließlich für
              Mandanten, deren einziger Owner der Gelöschte ist — bei Mit-Ownern
              und Mitgliedern läuft der Betrieb samt Abo unverändert weiter, und
              „Ihr Abo wird beendet" wäre eine falsche Geldzusage. */}
          {vorschau.mandanten.map((m) => {
            if (!m.einziger_owner) return null
            const kanal = abo(m)
            if (!kanal) return null
            return (
              <p key={`abo-${m.tenant_id}`} className="text-muted">
                {kanal === 'apple' ? t('kontoLoeschung.aboApple')
                  : kanal === 'google' ? t('kontoLoeschung.aboGoogle')
                  : t('kontoLoeschung.aboStripe')}
              </p>
            )
          })}

          <Field label={t('kontoLoeschung.passwort')} hint={t('kontoLoeschung.passwortHint')}>
            <Input
              type="password"
              value={passwort}
              autoComplete="current-password"
              onChange={(e) => { setFehler(null); setPasswort(e.target.value) }}
            />
          </Field>
          {fehler && (
            <p className="bg-danger-soft text-danger rounded-xl px-3 py-2">{fehler}</p>
          )}
        </div>
      )}
    </Sheet>
  )
}

/**
 * AK5: die Wahl vor allem anderen. Gerendert von `App.tsx`, sobald `auth.tsx`
 * beim Anmelden eine offene Vormerkung findet (`mode === 'loeschung'`) — vor
 * jedem App-Inhalt, vor dem Mandanten-Laden.
 *
 * „Löschung beibehalten" ist Auflage A6 wörtlich: Abmeldung, Vormerkung
 * unverändert — kein Fristneustart, keine neue Zeile.
 */
export function KontoLoeschungGate() {
  const { t, lang } = useI18n()
  const { loeschung, loeschungAbbrechen, signOut } = useAuth()
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const abbrechen = async () => {
    setBusy(true)
    setFehler(null)
    try {
      await loeschungAbbrechen()
    } catch {
      setFehler(t('kontoLoeschung.dialogFehler'))
      setBusy(false)
    }
    // Bei Erfolg schaltet `auth.tsx` den Modus um; dieses Element verschwindet.
  }

  return (
    <div className="min-h-dvh bg-surface-2 flex items-center justify-center p-4 pt-safe">
      <Card className="w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-2.5">
          <Logo size={30} />
          <h1 className="text-lg font-semibold">{t('kontoLoeschung.dialogTitel')}</h1>
        </div>
        <p className="text-sm">
          {loeschung?.fristEnde
            ? t('kontoLoeschung.dialogText', { datum: fristDatum(loeschung.fristEnde, lang) })
            : t('kontoLoeschung.dialogTextOhneDatum')}
        </p>
        {fehler && (
          <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{fehler}</p>
        )}
        <div className="flex flex-col gap-2">
          <Button variant="primary" onClick={() => void abbrechen()} disabled={busy}>
            {t('kontoLoeschung.dialogAbbrechen')}
          </Button>
          <Button variant="ghost" onClick={() => void signOut()} disabled={busy}>
            {t('kontoLoeschung.dialogBeibehalten')}
          </Button>
        </div>
      </Card>
    </div>
  )
}
